import React, { useMemo, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useAuth } from './contexts/AuthContext';
import { usePlan } from './contexts/PlanContext';
import { ToastProvider } from './contexts/ToastContext';
import { FaSpinner } from 'react-icons/fa';
import { Analytics } from '@vercel/analytics/react';
import CookieConsent from './components/CookieConsent';
import ErrorBoundary from './components/ErrorBoundary';

// Plan-based App versions
import AgentVFreeApp from './pages/modules/free/AgentV-Free';
import AgentVIndividualApp from './pages/modules/individual/AgentV-Individual';
import AgentVTeamApp from './pages/modules/team/AgentV-Team';
import BillingPage from './Billing';

// Public Pages (shared across all plans)
import Homepage from './pages/public/Homepage';
import FeaturesPage from './pages/public/FeaturesPage';
import PricingPage from './pages/public/PricingPage';
import ResourcesPage from './pages/public/ResourcesPage';
import ResourceViewer from './pages/public/ResourceViewer';
import AboutPage from './pages/public/AboutPage';
import ContactPage from './pages/public/ContactPage';
import WaitlistPage from './pages/public/WaitlistPage';
import PrivacyPolicy from './pages/public/PrivacyPolicy';
import TermsOfService from './pages/public/TermsOfService';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import TeamLogin from './pages/auth/TeamLogin';
import TeamSignup from './pages/auth/TeamSignup';
import JoinTeam from './pages/auth/JoinTeam';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import AccountLinking from './pages/AccountLinking';


// App styles
import './App.css';

// Loading component
const LoadingSpinner = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    background: '#f3f4f6'
  }}>
    <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '2rem', color: '#3b9efc' }} />
  </div>
);

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

// Plan-based App component - memoized to prevent unnecessary re-renders
const PlanBasedApp = () => {
  const { planType, loading } = usePlan();
  
  // Memoize the app selection to prevent unnecessary re-renders
  const selectedApp = useMemo(() => {
    if (loading) {
      return <LoadingSpinner />;
    }
    
    // Route to appropriate app based on plan
    switch (planType) {
      case 'Free':
        return <AgentVFreeApp />;
      case 'Individual':
        return <AgentVIndividualApp />;
      case 'Team':
        return <AgentVTeamApp />;
      default:
        return <AgentVFreeApp />;
    }
  }, [planType, loading]);
  
  return selectedApp;
};

// Lazy load components for better performance
const IndividualCompass = React.lazy(() => import('./pages/modules/individual/Compass').catch(() => ({ default: () => <div>Error loading Compass</div> })));
const IndividualKnowledgeHub = React.lazy(() => import('./pages/modules/individual/KnowledgeHub').catch(() => ({ default: () => <div>Error loading Knowledge Hub</div> })));
const IndividualSettings = React.lazy(() => import('./Settings').catch(() => ({ default: () => <div>Error loading Settings</div> })));

const TeamCompass = React.lazy(() => import('./pages/modules/team/Compass').catch(() => ({ default: () => <div>Error loading Compass</div> })));
const TeamKnowledgeHub = React.lazy(() => import('./pages/modules/team/KnowledgeHub').catch(() => ({ default: () => <div>Error loading Knowledge Hub</div> })));
const TeamSettings = React.lazy(() => import('./Settings').catch(() => ({ default: () => <div>Error loading Settings</div> })));

// ScrollToTop component - scrolls to top when route changes
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Main App component
function App() {
  const { user } = useAuth();
  const location = useLocation();

  // Set body attributes for CookieHub widget visibility and track page views
  useEffect(() => {
    const isAuthenticated = !!user;
    const currentPath = location.pathname;
    
    // Temporarily disable body attributes to test if they're causing compass issues
    // document.body.setAttribute('data-authenticated', isAuthenticated.toString());
    // document.body.setAttribute('data-route', currentPath);
    
    // Track page view for GA4
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'page_view',
        page_title: document.title,
        page_location: window.location.href,
        page_path: currentPath,
        user_authenticated: isAuthenticated
      });
    }
  }, [user, location.pathname]);

  return (
    <HelmetProvider>
      <ToastProvider>
        <ErrorBoundary>
          <ScrollToTop />
          <Routes>
          {/* Public Pages - Shared across all plans */}
          <Route path="/" element={user ? <Navigate to="/app/agent-v" /> : <Homepage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/resources/:s3Key" element={<ResourceViewer />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/waitlist" element={<WaitlistPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          
          {/* Authentication - Free and Individual plans */}
          <Route
            path="/login"
            element={!user ? <Login /> : <Navigate to="/app/agent-v" />}
          />
          <Route
            path="/signup"
            element={!user ? <Signup /> : <Navigate to="/app/agent-v" />}
          />
          
          {/* Authentication - Team plan */}
          <Route
            path="/team-login"
            element={!user ? <TeamLogin /> : <Navigate to="/app/agent-v" />}
          />
          <Route
            path="/team-signup"
            element={!user ? <TeamSignup /> : <Navigate to="/app/agent-v" />}
          />
          
          {/* Password Reset Routes */}
          <Route
            path="/forgot-password"
            element={!user ? <ForgotPassword /> : <Navigate to="/app/agent-v" />}
          />
          <Route
            path="/reset-password/:token"
            element={!user ? <ResetPassword /> : <Navigate to="/app/agent-v" />}
          />
          
          {/* Shared authentication routes */}
          <Route
            path="/join-team"
            element={!user ? <JoinTeam /> : <Navigate to="/app/agent-v" />}
          />
          <Route
            path="/account-linking"
            element={<AccountLinking />}
          />
          

          {/* Protected App Routes - Plan-based routing */}
          <Route
            path="/app/*"
            element={
              <ProtectedRoute>
                <PlanBasedApp />
              </ProtectedRoute>
            }
          />
          
          {/* 404 Fallback - Must be last */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        
        {/* Cookie Consent Management */}
        <CookieConsent />
        
        <Analytics />
        </ErrorBoundary>
      </ToastProvider>
    </HelmetProvider>
  );
}

export default App;
